import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, X } from "lucide-react";
import { MARKETS_BY_CODE, countryToMarket, setStoredMarket } from "@/i18n/markets";
import { setLanguage, type SupportedLanguage } from "@/i18n";
import { BRAND_GREEN, BRAND_GREEN_SOFT_08 } from "@/lib/design-tokens";
import { useLocale } from "@/i18n/LocaleContext";
import { useGeoHint } from "@/hooks/useCars";
import { DRIVESHARE_REDIRECT_URL, getDriveShareRedirectUrl } from "@/lib/driveshareRedirect";

const DISMISS_KEY = "bac.regionBannerDismissed";
const SUPPORTED_LANG_CODES = new Set(["en","fr","de","es","it","nl","da","sv","no"]);
const ENTER_DELAY_MS = 350;
const EXIT_DURATION_MS = 240;
const ANCHOR_SELECTOR = "[data-region-anchor]";
const PANEL_WIDTH = 360;
const PANEL_GAP = 12;

interface AnchorRect { top: number; right: number; left: number; width: number; height: number; }

function findVisibleAnchor(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const nodes = document.querySelectorAll<HTMLElement>(ANCHOR_SELECTOR);
  for (const el of Array.from(nodes)) {
    if (el.offsetParent !== null && el.getBoundingClientRect().width > 0) return el;
  }
  return null;
}

export function RegionSuggestionBanner() {
  const { t, i18n } = useTranslation();
  const { market: urlMarket, switchLocale } = useLocale();
  const [suggestion, setSuggestion] = useState<{ market: string; lang: string } | null>(null);
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);
  const [anchorRect, setAnchorRect] = useState<AnchorRect | null>(null);
  const anchorElRef = useRef<HTMLElement | null>(null);

  // Shared with HomePage so a US visitor only triggers the geo lookup once
  // per session.
  const { data: geoHint } = useGeoHint();

  useEffect(() => {
    if (typeof window === "undefined") return;
    try { if (window.localStorage.getItem(DISMISS_KEY)) return; } catch { return; }

    // If the URL already pins a market we trust the user — no nudge.
    if (urlMarket) return;

    if (!geoHint) return;

    const currentLang = (i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);
    const suggestedMarket = countryToMarket(geoHint.country);
    const suggestedLang = geoHint.language && SUPPORTED_LANG_CODES.has(geoHint.language) ? geoHint.language : null;
    if (!suggestedMarket) return;
    const market = suggestedMarket;
    const lang = suggestedLang || MARKETS_BY_CODE[market]?.defaultLanguage || "en";
    // Don't nudge if the suggestion matches the current language already
    // — e.g. a German visitor on bare bookaclassic.com whose UI is already
    // in German shouldn't see a banner saying "switch to German".
    if (market === urlMarket && lang === currentLang) return;
    setSuggestion({ market, lang });
  }, [urlMarket, i18n.language, i18n.resolvedLanguage, geoHint]);

  // Resolve anchor position once a suggestion is ready, and track scroll/resize.
  useEffect(() => {
    if (!suggestion) return;
    let raf = 0;

    const measure = () => {
      const el = findVisibleAnchor();
      anchorElRef.current = el;
      if (!el) { setAnchorRect(null); return; }
      const r = el.getBoundingClientRect();
      setAnchorRect({ top: r.top, right: r.right, left: r.left, width: r.width, height: r.height });
    };

    // Wait a frame so the header has laid out, then poll briefly until anchor exists.
    let attempts = 0;
    const tryMeasure = () => {
      measure();
      if (!anchorElRef.current && attempts < 20) {
        attempts++;
        raf = window.requestAnimationFrame(tryMeasure);
      }
    };
    tryMeasure();

    const onScrollOrResize = () => measure();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize);
    };
  }, [suggestion]);

  // Mount + animate-in once a suggestion is ready (we can mount even without an
  // anchor — we'll fall back to a centered drop from the top of the viewport).
  useEffect(() => {
    if (!suggestion) return;
    setMounted(true);
    const id = window.setTimeout(() => setEntered(true), ENTER_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [suggestion]);

  // Briefly highlight the globe icon so the user's eye connects the popover
  // back to its origin (only while the panel is visible).
  useEffect(() => {
    if (!entered) return;
    const el = anchorElRef.current;
    if (!el) return;
    el.classList.add("region-anchor-pulse");
    return () => { el.classList.remove("region-anchor-pulse"); };
  }, [entered]);

  const dissolveOut = (after: () => void) => {
    setEntered(false);
    window.setTimeout(() => {
      setMounted(false);
      setSuggestion(null);
      after();
    }, EXIT_DURATION_MS);
  };

  if (!suggestion || !mounted) return null;
  const market = MARKETS_BY_CODE[suggestion.market];
  if (!market) return null;

  const accept = async () => {
    try { window.localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
    if (suggestion.market === "us") {
      // Cross-brand handoff to driveshare.com.
      const target = await getDriveShareRedirectUrl().catch(() => DRIVESHARE_REDIRECT_URL);
      dissolveOut(() => {
        if (typeof window !== "undefined") window.location.assign(target);
      });
      return;
    }
    setStoredMarket(suggestion.market);
    setLanguage(suggestion.lang as SupportedLanguage);
    const marketDefault = MARKETS_BY_CODE[suggestion.market]?.defaultLanguage;
    const explicitLang = suggestion.lang === marketDefault ? null : suggestion.lang;
    dissolveOut(() => {
      switchLocale({ market: suggestion.market, language: explicitLang });
    });
  };

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
    dissolveOut(() => { /* noop */ });
  };

  const langLabel = suggestion.lang.toUpperCase();

  // Compute panel position. If we have an anchor, drop down from it (right-aligned
  // to the anchor's right edge). Otherwise, fall back to top-center under the header.
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const sideMargin = 16;
  const panelWidth = Math.min(PANEL_WIDTH, vw - sideMargin * 2);
  let top = 72;
  let left = Math.max(sideMargin, (vw - panelWidth) / 2);
  let pointerLeft = panelWidth / 2;
  let originX: "left" | "right" | "center" = "center";

  if (anchorRect) {
    top = anchorRect.top + anchorRect.height + PANEL_GAP;
    // Right-align the panel to the anchor's right edge so it visually drops from the globe.
    let rawLeft = anchorRect.right - panelWidth;
    rawLeft = Math.max(sideMargin, Math.min(rawLeft, vw - sideMargin - panelWidth));
    left = rawLeft;
    const anchorCenterX = anchorRect.left + anchorRect.width / 2;
    pointerLeft = Math.max(16, Math.min(panelWidth - 16, anchorCenterX - left));
    originX = "right";
  }

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("language.suggest_title", { market: market.label, lang: langLabel })}
      className="region-suggest-root pointer-events-none fixed inset-0 z-50"
      data-testid="region-suggestion-banner"
    >
      <div
        className={[
          "region-suggest-panel pointer-events-auto absolute",
          entered ? "is-entered" : "is-leaving",
        ].join(" ")}
        style={{
          top,
          left,
          width: panelWidth,
          transformOrigin: `${originX === "right" ? "calc(100% - 20px)" : `${pointerLeft}px`} top`,
        }}
      >
        {/* Drop ribbon — a thin line that "falls" from the globe to the panel */}
        {anchorRect && (
          <span
            aria-hidden="true"
            className="region-suggest-ribbon"
            style={{
              left: pointerLeft,
              top: -PANEL_GAP,
              height: PANEL_GAP,
              backgroundColor: BRAND_GREEN,
            }}
          />
        )}

        {/* Pointer / caret */}
        <span
          aria-hidden="true"
          className="region-suggest-caret"
          style={{ left: pointerLeft - 7 }}
        />

        <div className="relative rounded-2xl bg-white p-4 shadow-[0_18px_48px_-12px_rgba(0,0,0,0.22),0_4px_12px_rgba(0,0,0,0.06)] ring-1 ring-gray-200/80">
          <button
            onClick={dismiss}
            aria-label={t("common.close", { defaultValue: "Close" })}
            className="cursor-pointer absolute right-2 top-2 rounded-full p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>

          <div className="flex items-start gap-3 pr-7">
            <div
              className="shrink-0"
              style={{ color: BRAND_GREEN }}
              aria-hidden="true"
            >
              <Globe className="h-7 w-7" strokeWidth={1.75} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-semibold text-gray-900 leading-snug">
                {t("language.suggest_title", { market: market.label, lang: langLabel })}
              </p>
              <p className="mt-1 text-[13px] text-gray-500 leading-snug">
                {t("language.suggest_subtitle")}
              </p>
            </div>
          </div>

          <div className="mt-3.5 flex items-center gap-2">
            <button
              onClick={accept}
              className="inline-flex flex-1 items-center justify-center rounded-lg px-3.5 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
              style={{ backgroundColor: BRAND_GREEN }}
              data-testid="button-region-suggest-accept"
            >
              {t("language.suggest_accept")}
            </button>
            <button
              onClick={dismiss}
              className="cursor-pointer inline-flex items-center justify-center rounded-lg px-3.5 py-2 text-[13px] font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
              data-testid="button-region-suggest-dismiss"
            >
              {t("language.suggest_dismiss")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
